import GeoJsonFetchLayer from './GeoJsonFetchLayer';
import { centroidOfGeometry } from '../utils/coords';
import { addOrUpdateGeoJsonSource, removeSourceAndLayers } from '../utils/maplibre-helpers';
import { PARCEL_SELECTED_STYLE } from './DzialkiLayer';
import { BUILDING_SELECTED_STYLE } from './BudynkiLayer';

function getFeatureCollectionCenter(data) {
    if (!data || !Array.isArray(data.features) || !data.features.length) {
        return null;
    }

    try {
        const bounds = new maplibregl.LngLatBounds();
        let hasBounds = false;

        data.features.forEach((feature) => {
            if (!feature || !feature.geometry) {
                return;
            }

            const center = centroidOfGeometry(feature.geometry);
            if (!center) {
                return;
            }

            bounds.extend(center);
            hasBounds = true;
        });

        return hasBounds ? {
            lat: bounds.getCenter().lat,
            lng: bounds.getCenter().lng
        } : null;
    } catch (_error) {
        return null;
    }
}

export default class SelectionPinLayer extends GeoJsonFetchLayer {
    static ID = 'selection-pin';
    static DESCRIPTION = 'Zaznaczony obiekt';
    static SOURCE_ID = 'src-selection-pin';
    static LAYER_IDS = ['lyr-selection-pin-fill', 'lyr-selection-pin-line'];

    getSourceId() {
        return SelectionPinLayer.SOURCE_ID;
    }

    getLayerIds() {
        return SelectionPinLayer.LAYER_IDS;
    }

    canRender() {
        const map = this.getMap();
        if (!map) {
            return false;
        }

        if (this.config.getActiveTab && this.config.getActiveTab() !== 'nearby') {
            return false;
        }

        return Math.round(map.getZoom() * 10) / 10 < this.config.overlayLayersMinZoom;
    }

    buildUrl() {
        if (this.config.getSelectedDid && this.config.getSelectedDid()) {
            return 'api/dzialki?pin_only=1&id_dzialki=' + encodeURIComponent(this.config.getSelectedDid());
        }

        if (this.config.getSelectedBid && this.config.getSelectedBid()) {
            return 'api/budynki?pin_only=1&id_budynku=' + encodeURIComponent(this.config.getSelectedBid());
        }

        return null;
    }

    getSelectedLayerType() {
        if (this.config.getSelectedDid && this.config.getSelectedDid()) {
            return 'parcel';
        }

        if (this.config.getSelectedBid && this.config.getSelectedBid()) {
            return 'building';
        }

        return null;
    }

    getStyle(layerType) {
        return layerType === 'parcel' ? PARCEL_SELECTED_STYLE : BUILDING_SELECTED_STYLE;
    }

    clear() {
        this.abortPending();

        const map = this.getMap();
        if (map) {
            removeSourceAndLayers(map, this.getSourceId(), this.getLayerIds());
        }

        if (this.config.setLayer) {
            this.config.setLayer(null);
        }
    }

    renderSelectionData(data, layerType) {
        const map = this.getMap();
        if (!map || !data || !Array.isArray(data.features) || !data.features.length || !layerType) {
            this.clear();
            return null;
        }

        const style = this.getStyle(layerType);
        removeSourceAndLayers(map, this.getSourceId(), this.getLayerIds());

        addOrUpdateGeoJsonSource(map, this.getSourceId(), data);
        map.addLayer({
            id: SelectionPinLayer.LAYER_IDS[0],
            type: 'fill',
            source: this.getSourceId(),
            paint: {
                'fill-color': style.fillColor,
                'fill-opacity': style.fillOpacity
            }
        });
        map.addLayer({
            id: SelectionPinLayer.LAYER_IDS[1],
            type: 'line',
            source: this.getSourceId(),
            paint: {
                'line-color': style.color,
                'line-width': style.weight
            }
        });

        if (this.config.setLayer) {
            this.config.setLayer(this.getSourceId());
        }

        return {
            layer: this.getSourceId(),
            center: getFeatureCollectionCenter(data)
        };
    }

    load() {
        const layerType = this.getSelectedLayerType();
        const url = this.buildUrl();
        if (!this.canRender() || !url) {
            this.clear();
            return;
        }

        this.abortPending();
        const controller = new AbortController();
        this.setAbortController(controller);

        fetch(url, { signal: controller.signal })
            .then((response) => response.json())
            .then((data) => {
                if (controller.signal.aborted || !this.canRender()) {
                    return;
                }

                const result = this.renderSelectionData(data, layerType);
                if (this.config.onRendered) {
                    this.config.onRendered(data, result, this);
                }
            })
            .catch((error) => {
                if (error && error.name === 'AbortError') {
                    return;
                }

                this.handleError(error);
            })
            .finally(() => {
                if (this.getAbortController() === controller) {
                    this.setAbortController(null);
                }
            });
    }
}
